import axios from 'axios';

// Private Capital Infrastructure Ops API
// Proxied through same-origin nginx: /ops-api/ → port 6101 /api/v1/admin/ops/
const opsApi = axios.create({
  baseURL: '/ops-api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// Use ops API key for cross-platform auth
opsApi.interceptors.request.use((config) => {
  const apiKey = import.meta.env.VITE_OPS_API_KEY || localStorage.getItem('ops_api_key');
  if (apiKey) {
    config.headers['X-Ops-Key'] = apiKey;
  }
  // Also try the admin token from the Private Capital platform if logged in
  const token = localStorage.getItem('redex_token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface HealthData {
  overall: string;
  checks: {
    database: { status: string; latencyMs?: number; tenants?: number };
    databases?: Record<string, { status: string; latencyMs: number; database: string }>;
    paymentProvider: { status: string; provider: string; environment: string; latencyMs: number };
    connectionPool: { status: string; total: number; idle: number; waiting: number };
    memory: { heapUsedMB: number; heapTotalMB: number; rssMB: number; externalMB: number };
    uptime: { processSeconds: number; processHours: number };
  };
  timestamp: string;
}

export interface ThroughputData {
  ach: Record<string, string>;
  investments: Record<string, string>;
  engagements: Record<string, string>;
  users: Record<string, string>;
  byTenant?: Record<string, Record<string, Record<string, string>>>;
  tenants?: Array<{ id: string; label: string }>;
  timestamp: string;
}

export interface ErrorRateData {
  ach: { total: string; failed: string; failure_rate_pct: string };
  recentFailures: Array<{ id: string; type: string; status: string; amount_cents: number; created_at: string; description: string }>;
  auditActivity: Array<{ action: string; count: string }>;
  reconciliation: { total_mismatches: string; total_synced: string; total_errors: string; total_runs: string };
}

export interface ReconciliationRun {
  id: number;
  checked: number;
  mismatches: number;
  synced: number;
  errors: number;
  lookback_hours: number;
  auto_fix: boolean;
  duration_ms: number;
  details: any;
  created_at: string;
}

export interface EnvironmentData {
  environment: string;
  tenant?: string;
  tenants?: Array<{ id: string; label: string; database: string }>;
  features: Record<string, boolean>;
  payments: { provider: string; sandbox: boolean; environment?: string };
  rateLimits?: Record<string, number>;
  logging?: { level: string; structured: boolean };
  database?: { host: string; name: string; poolMax: number };
  nodeVersion?: string;
  pid?: number;
}

export interface ProviderStatusData {
  provider: string;
  connected: boolean;
  environment: string;
  latencyMs: number;
  lastCheck: string;
  error?: string;
}

export const opsService = {
  getHealth: () => opsApi.get<{ success: boolean; data: HealthData }>('/health'),
  getThroughput: () => opsApi.get<{ success: boolean; data: ThroughputData }>('/throughput'),
  getErrorRates: () => opsApi.get<{ success: boolean; data: ErrorRateData }>('/error-rates'),
  getReconciliationRuns: () => opsApi.get<{ success: boolean; data: ReconciliationRun[] }>('/reconciliation/runs'),
  triggerReconciliation: (lookbackHours = 24, autoFix = false) =>
    opsApi.post<{ success: boolean; data: ReconciliationRun }>('/reconciliation/run', { lookbackHours, autoFix }),
  getEnvironment: () => opsApi.get<{ success: boolean; data: EnvironmentData }>('/environment'),
  getProviderStatus: () => opsApi.get<{ success: boolean; data: ProviderStatusData }>('/provider-status'),
};

export default opsService;
